import { ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { hasChanges, lineDiff } from "../../../shared/diff/lineDiff";
import AppButton from "../../buttons/AppButton";
import ModalShell from "../../overlays/ModalShell";
import { classNames } from "../../styles/classNames";
import InlineDiff from "./InlineDiff";
import type { SourceLanguage } from "./sourceLanguage";
import { FullSource, SourceToolbar } from "./SourceView";

interface SourceDiffProps {
  /** Names the comparison for screen readers and titles its full view. */
  label: string;
  language: SourceLanguage;
  /** The text before; empty for something added. */
  original: string;
  /** The text after; empty for something removed. */
  modified: string;
  /**
   * `inline`: the light line comparison, for history rows and lists, with a button that
   * opens the full one. `full`: the editor's comparison, side by side or unified, with
   * find, folding and previous/next change.
   */
  presentation?: "inline" | "full";
  /** The tallest a full comparison grows, in pixels. */
  maxHeight?: number;
  className?: string;
}

/** Two versions of one source text, compared line by line. */
export default function SourceDiff({ label, language, original, modified, presentation = "inline", maxHeight = 560, className }: SourceDiffProps) {
  const { t } = useTranslation();
  const [fullOpen, setFullOpen] = useState(false);
  if (presentation === "full") {
    return (
      <div className={classNames("space-y-1", className)}>
        <SourceToolbar value={modified} />
        <FullSource kind="diff" label={label} language={language} original={original} modified={modified} maxHeight={maxHeight} />
      </div>
    );
  }
  return (
    <div className={classNames("space-y-1", className)}>
      <SourceToolbar value={modified}>
        <AppButton size="xs" variant="ghost" onClick={() => { setFullOpen(true); }}>
          <ArrowsPointingOutIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {t("common.source.openComparison")}
        </AppButton>
      </SourceToolbar>
      <LightDiff label={label} original={original} modified={modified} />
      {fullOpen ? (
        <ModalShell title={label} onClose={() => { setFullOpen(false); }} size="6xl">
          <FullSource kind="diff" label={label} language={language} original={original} modified={modified} maxHeight={640} />
        </ModalShell>
      ) : null}
    </div>
  );
}

function LightDiff({ label, original, modified }: { label: string; original: string; modified: string }) {
  const { t } = useTranslation();
  const lines = useMemo(() => lineDiff(original, modified), [original, modified]);
  return hasChanges(lines) ? <InlineDiff lines={lines} label={label} /> : <p className="app-text-muted text-xs">{t("common.source.noChanges")}</p>;
}
